import { useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePlatformStore } from "@platform/store";
import {
  useFetchActions,
  getAction,
  isActionCompleted,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
  useUpdateActionStatus,
} from "@core/features/actions";
import {
  ProfileTabs,
  ProfilePublicProfileTab,
  ProfileMatchingFormTab,
  PublicProfileSheetContainer,
  type TabValue,
} from "@platform/features/programProfile";
import { ProgramDialogs } from "@platform/features/programDialogs";
import { usePublicProfileSheet } from "@platform/store";
import { apiRequest } from "@core/services/api/apiRequest";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { Button } from "@themes/pollinator/ui/Button";

const VALID_TABS: TabValue[] = ["profile", "matching"];

export type Props = {
  /**
   * Optional callback when profile is saved successfully.
   */
  onSaveSuccess?: () => void;
};

/**
 * Profile page with tabbed navigation.
 *
 * Routes:
 * - /program/:id/profile → Public Profile tab (default)
 * - /program/:id/profile/matching → Matching Form tab
 */
export function ProfilePage({ onSaveSuccess }: Props) {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const programId = id ? Number(id) : undefined;

  const user = usePlatformStore((s) => s.user);
  const { open: openPreviewSheet } = usePublicProfileSheet();
  const queryClient = useQueryClient();

  // Fetch actions to find the profile action
  const { data: actionsData } = useFetchActions({
    programId: programId ?? 0,
    user,
    isEnabled: !!programId && !!user,
  });

  // Get all profile actions that have a quiz attached
  const profileQuizActions = useMemo(() => {
    if (!actionsData?.actions) return [];
    return actionsData.actions.filter(
      (action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.PROFILE_PARTICIPANT &&
        action.target_object_type === "quiz" &&
        action.target_object_id
    );
  }, [actionsData?.actions]);

  // Get the main profile_participant action (for marking completed on save)
  const profileAction = useMemo(() => {
    if (!actionsData?.actions) return undefined;
    return getAction(actionsData.actions, ACTION_TYPE_SLUG.PROFILE_PARTICIPANT);
  }, [actionsData?.actions]);

  const matchingQuizAction = actionsData?.actions
    ? getAction(actionsData.actions, ACTION_TYPE_SLUG.MATCHING_QUIZ)
    : undefined;

  // Extract quiz ID from matching quiz action
  const quizId = matchingQuizAction
    ? ((matchingQuizAction.target_object_data as Record<string, unknown>)
        ?.quiz as number) ?? matchingQuizAction.target_object_id
    : undefined;

  // Separate hook for matching quiz action status
  const { mutate: updateMatchingQuizStatus } = useUpdateActionStatus({
    actionId: matchingQuizAction?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: matchingQuizAction?.current_status?.name,
  });

  // Handle profile quiz save - update action status via direct API call
  // (can't use hooks in loops, so we use apiRequest directly)
  const handleProfileQuizSaveSuccess = async (actionId: number) => {
    if (actionId) {
      const actionForQuiz = actionsData?.actions?.find((a) => a.id === actionId);
      if (!actionForQuiz || !isActionCompleted(actionForQuiz)) {
        await apiRequest(`/actions/${actionId}/status/`, "POST", {
          status: ACTION_STATUS.COMPLETED,
        });
      }
    }
    // Also complete the main profile action if it's a different action
    if (profileAction && profileAction.id !== actionId && !isActionCompleted(profileAction)) {
      await apiRequest(`/actions/${profileAction.id}/status/`, "POST", {
        status: ACTION_STATUS.COMPLETED,
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["actions"] });
    onSaveSuccess?.();
  };

  // Handle basic profile save - mark the profile action as completed
  const handleProfileSaveSuccess = async () => {
    if (profileAction && !isActionCompleted(profileAction)) {
      await apiRequest(`/actions/${profileAction.id}/status/`, "POST", {
        status: ACTION_STATUS.COMPLETED,
      });
      await queryClient.invalidateQueries({ queryKey: ["actions"] });
    }
    onSaveSuccess?.();
  };

  const handleMatchingQuizSaveSuccess = () => {
    if (matchingQuizAction?.id) {
      updateMatchingQuizStatus();
    }
  };

  // Determine active tab - default to "profile" if no tab specified
  const activeTab: TabValue =
    tab && VALID_TABS.includes(tab as TabValue)
      ? (tab as TabValue)
      : "profile";

  // Whether to show the matching form tab
  const showMatchingTab = !!matchingQuizAction;

  // Redirect to valid tab if invalid tab in URL, or if trying to access matching tab when not available
  if (tab && (!VALID_TABS.includes(tab as TabValue) || (tab === "matching" && !showMatchingTab))) {
    return <Navigate to={`/program/${id}/profile`} replace />;
  }

  const handleTabChange = (newTab: TabValue) => {
    if (newTab === "profile") {
      navigate(`/program/${id}/profile`);
    } else {
      navigate(`/program/${id}/profile/${newTab}`);
    }
  };

  return (
    <>
      <AppPageHeader
        title="Profile"
        description="Manage your personal information and preferences."
        tabs={
          <ProfileTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showMatchingTab={showMatchingTab}
          />
        }
        actions={
          activeTab === "profile" ? (
            <Button
              variant="secondary"
              onClick={openPreviewSheet}
              data-testid="preview-public-profile-button"
            >
              Preview Public Profile
            </Button>
          ) : undefined
        }
      />
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        {activeTab === "profile" && (
          <ProfilePublicProfileTab
            programId={programId}
            profileQuizActions={profileQuizActions}
            onProfileQuizSaveSuccess={handleProfileQuizSaveSuccess}
            onProfileSaveSuccess={handleProfileSaveSuccess}
          />
        )}

        {activeTab === "matching" && (
          <ProfileMatchingFormTab
            quizId={quizId}
            onSaveSuccess={handleMatchingQuizSaveSuccess}
          />
        )}
      </OuterContentContainer>
      <PublicProfileSheetContainer programId={programId} />
      <ProgramDialogs />
    </>
  );
}

export default ProfilePage;
